import { useQuery } from '@tanstack/react-query';
import { AnalyticalPanel, DataQualityState } from '@atlas/ui';
import { api } from '../../lib/api';
import { ExplainActions, PageHeader } from '../../components/shared';
import { MetricExplainer } from '../../components/explain';
import { PageSkeleton } from '../../components/PageSkeleton';

export function LearningPage() {
  const { data, isLoading, isError } = useQuery({ queryKey: ['learning'], queryFn: api.learning });

  if (isLoading) return <PageSkeleton blocks={3} />;
  if (isError || !data) return <DataQualityState state="unavailable" title="Learning overview unavailable" />;

  return (
    <div className="space-y-6">
      <PageHeader title="Learn" subtitle="Counterfactuals, calibration, and policy proposals." />
      <ExplainActions screenName="Learn" />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricExplainer
          label="Brier score"
          value={data.brierScore?.toFixed(2) ?? '—'}
          glossaryTermId="glossary.brier-score"
          explainBody="Live decisions only — proxy outcomes excluded."
        />
        <MetricExplainer
          label="Mature decisions"
          value={String(data.matureDecisionCount ?? '—')}
        />
        <MetricExplainer
          label="Avoided losses"
          value={String(data.decisionValue?.avoidedLossValue ?? '—')}
          unit="USD"
        />
        <MetricExplainer
          label="Proxy separated"
          value={data.proxySeparated ? 'Yes' : 'No'}
          glossaryTermId="glossary.proxy-live-separation"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <AnalyticalPanel title="Calibration buckets" subtitle="Live decisions">
          <ul className="space-y-2 text-sm">
            {(data.calibration ?? []).map((b) => (
              <li
                key={b.bucket}
                className="flex justify-between rounded-[var(--radius-control)] border border-[var(--color-border-default)] px-3 py-2"
              >
                <span>{b.bucket}</span>
                <span className="font-mono text-[var(--color-signal-cyan)]">
                  {b.realizedFrequency?.toFixed(2)} / n={b.sampleCount}
                </span>
              </li>
            ))}
          </ul>
        </AnalyticalPanel>

        <AnalyticalPanel title="Counterfactual ledger" subtitle="Proxy vs live segregated">
          <p className="mb-3 text-xs text-[var(--color-warning)]">{data.liveProxySeparationNote}</p>
          <DataQualityState state="partial" title="Proxy channel" description="Tactical lab outcomes tracked separately from live P&L." />
          <ul className="mt-4 space-y-2 text-sm text-[var(--color-text-secondary)]">
            {(data.sampleCaveats ?? []).map((c) => (
              <li key={c}>◇ {c}</li>
            ))}
          </ul>
        </AnalyticalPanel>
      </div>
    </div>
  );
}
